import { useMemo, useState } from "react";
import { differenceInCalendarDays, format, startOfDay } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Calendar } from "./ui/calendar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { CheckCircle2, Loader2, MessageCircle } from "lucide-react";
import { DECOR_OPTIONS, PET_FEE, WHATSAPP_NUMBER, formatBRL } from "../data/chalets";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
    full_name: "",
    birth_date: "",
    cpf: "",
    email: "",
    phone: "",
    companion_name: "",
    guests: "2",
    pet: "nao",
    decor: "nao",
    decor_option: "",
    payment_method: "pix",
    installments: "1",
    special_requests: "",
};

export const BookingDialog = ({ chalet, open, onOpenChange }) => {
    const [range, setRange] = useState({ from: undefined, to: undefined });
    const [form, setForm] = useState(initialForm);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(null);
    const [error, setError] = useState("");

    const nights = useMemo(
        () => (range.from && range.to ? differenceInCalendarDays(range.to, range.from) : 0),
        [range]
    );
    const decorOption = DECOR_OPTIONS.find((d) => d.id === form.decor_option);
    const decorPrice = form.decor === "sim" && decorOption ? decorOption.price : 0;
    const petPrice = form.pet === "sim" ? PET_FEE : 0;
    const stayTotal = nights * chalet.price;
    const total = stayTotal + petPrice + decorPrice;
    const installments = parseInt(form.installments, 10);

    const set = (key) => (e) => {
        const value = e && e.target ? e.target.value : e;
        setForm((f) => ({ ...f, [key]: value }));
    };

    const reset = () => {
        setRange({ from: undefined, to: undefined });
        setForm(initialForm);
        setSuccess(null);
        setError("");
    };

    const buildMessage = () => {
        const lines = [
            "*NOVA RESERVA - RARITY CHALÉS*",
            "",
            `Chalé: ${chalet.name}`,
            `Check-in: ${format(range.from, "dd/MM/yyyy")}`,
            `Check-out: ${format(range.to, "dd/MM/yyyy")}`,
            `Diárias: ${nights} x ${formatBRL(chalet.price)} = ${formatBRL(stayTotal)}`,
            `Pet: ${form.pet === "sim" ? `Sim (+${formatBRL(PET_FEE)})` : "Não"}`,
            `Decoração romântica: ${
                form.decor === "sim" && decorOption
                    ? `${decorOption.name} (+${formatBRL(decorOption.price)})`
                    : "Não"
            }`,
            `*VALOR TOTAL: ${formatBRL(total)}*`,
            `Pagamento: ${
                form.payment_method === "pix"
                    ? "Pix"
                    : `Cartão de crédito em ${installments}x de ${formatBRL(total / installments)} (sem juros)`
            }`,
            "",
            `Nome: ${form.full_name}`,
            `Data de nascimento: ${form.birth_date}`,
            `CPF: ${form.cpf}`,
            `E-mail: ${form.email}`,
            `Telefone: ${form.phone}`,
            `Acompanhante: ${form.companion_name}`,
            `Hóspedes: ${form.guests}`,
        ];
        if (form.special_requests) lines.push(`Solicitação especial: ${form.special_requests}`);
        return lines.join("\n");
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        const payload = {
            chalet_id: chalet.id,
            chalet_name: chalet.name,
            check_in: format(range.from, "yyyy-MM-dd"),
            check_out: format(range.to, "yyyy-MM-dd"),
            nights,
            daily_rate: chalet.price,
            stay_total: stayTotal,
            pet: form.pet === "sim",
            pet_fee: petPrice,
            decor: form.decor === "sim",
            decor_option: form.decor === "sim" && decorOption ? decorOption.name : "",
            decor_price: decorPrice,
            payment_method: form.payment_method,
            installments: form.payment_method === "cartao" ? installments : 1,
            total,
            full_name: form.full_name,
            birth_date: form.birth_date,
            cpf: form.cpf,
            email: form.email,
            phone: form.phone,
            companion_name: form.companion_name,
            guests: parseInt(form.guests, 10),
            special_requests: form.special_requests,
        };
        try {
            const res = await fetch(`${API}/reservations`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            if (!res.ok) throw new Error("request failed");
            const data = await res.json();
            setSuccess(data);
        } catch {
            setError("Não foi possível concluir a reserva. Tente novamente ou chame no WhatsApp.");
        } finally {
            setLoading(false);
        }
    };

    const openWhatsApp = () => {
        window.open(
            `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(buildMessage())}`,
            "_blank"
        );
    };

    const datesSelected = nights > 0;
    const decorValid = form.decor === "nao" || !!decorOption;

    return (
        <Dialog
            open={open}
            onOpenChange={(v) => {
                onOpenChange(v);
                if (!v) setTimeout(reset, 300);
            }}
        >
            <DialogContent
                data-testid={`booking-dialog-${chalet.id}`}
                className="max-w-3xl max-h-[92vh] overflow-y-auto bg-[#FDFBF7] border-[#1B4332]/15 rounded-sm"
            >
                {success ? (
                    <div className="py-10 px-2 text-center" data-testid="booking-success">
                        <CheckCircle2 className="w-16 h-16 text-[#1B4332] mx-auto" strokeWidth={1.5} />
                        <h3 className="mt-6 text-2xl sm:text-3xl font-bold tracking-tight text-[#1B231A]">
                            Sua reserva foi concluída com Sucesso!
                        </h3>
                        <p className="mt-4 text-base text-[#3E4C3D] max-w-md mx-auto">
                            Recebemos os seus dados. Para confirmar, envie os dados da reserva pelo WhatsApp.
                        </p>
                        <div className="mt-6 inline-block text-left bg-[#F3F0E9] border border-[#1B4332]/10 rounded-sm px-6 py-4 text-sm text-[#3E4C3D]">
                            <p><strong>{chalet.name}</strong></p>
                            <p>
                                {format(range.from, "dd/MM/yyyy")} → {format(range.to, "dd/MM/yyyy")} · {nights}{" "}
                                {nights === 1 ? "diária" : "diárias"}
                            </p>
                            <p className="mt-1 text-lg font-bold text-[#1B4332]">Total: {formatBRL(total)}</p>
                        </div>
                        <div className="mt-8 flex justify-center gap-4 flex-wrap">
                            <button
                                data-testid="success-whatsapp-button"
                                onClick={openWhatsApp}
                                className="flex items-center gap-2 bg-[#1B4332] hover:bg-[#2D6A4F] text-white font-bold text-sm px-8 py-4 rounded-full transition-colors duration-300"
                            >
                                <MessageCircle className="w-4 h-4" strokeWidth={1.5} />
                                Enviar dados pelo WhatsApp
                            </button>
                            <button
                                data-testid="success-close-button"
                                onClick={() => onOpenChange(false)}
                                className="border border-[#1B4332]/30 text-[#1B4332] font-bold text-sm px-8 py-4 rounded-full hover:bg-[#1B4332]/5 transition-colors duration-300"
                            >
                                Fechar
                            </button>
                        </div>
                    </div>
                ) : (
                    <>
                        <DialogHeader>
                            <DialogTitle className="text-2xl sm:text-3xl font-bold tracking-tight text-[#1B231A]">
                                Reservar {chalet.name}
                            </DialogTitle>
                            <DialogDescription className="text-sm text-[#6C7A6B]">
                                Escolha as datas no calendário abaixo e preencha seus dados. Sem pagamento online.
                            </DialogDescription>
                        </DialogHeader>

                        <form onSubmit={handleSubmit} className="space-y-8">
                            <div data-testid="booking-calendar-section" className="border border-[#1B4332]/10 rounded-sm p-4 bg-white/60">
                                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8B5A2B] mb-3">
                                    1. Escolha as datas
                                </p>
                                <div className="flex justify-center">
                                    <Calendar
                                        mode="range"
                                        selected={range}
                                        onSelect={setRange}
                                        locale={ptBR}
                                        numberOfMonths={1}
                                        disabled={{ before: startOfDay(new Date()) }}
                                        data-testid="booking-calendar"
                                    />
                                </div>
                                <p className="text-center text-sm text-[#3E4C3D] mt-2" data-testid="booking-dates-summary">
                                    {range.from && range.to
                                        ? `Check-in ${format(range.from, "dd/MM/yyyy")} · Check-out ${format(range.to, "dd/MM/yyyy")} · ${nights} ${nights === 1 ? "diária" : "diárias"}`
                                        : range.from
                                          ? `Check-in ${format(range.from, "dd/MM/yyyy")} · selecione o check-out`
                                          : "Selecione o dia de check-in e depois o de check-out"}
                                </p>
                            </div>

                            <div className="grid sm:grid-cols-2 gap-4">
                                <p className="sm:col-span-2 text-xs font-bold uppercase tracking-[0.2em] text-[#8B5A2B]">
                                    2. Seus dados
                                </p>
                                <div className="space-y-1.5">
                                    <Label htmlFor="full_name">Nome Completo *</Label>
                                    <Input id="full_name" data-testid="input-full-name" required value={form.full_name} onChange={set("full_name")} />
                                </div>
                                <div className="space-y-1.5">
                                    <Label htmlFor="birth_date">Data de nascimento *</Label>
                                    <Input id="birth_date" data-testid="input-birth-date" type="date" required value={form.birth_date} onChange={set("birth_date")} />
                                </div>
                                <div className="space-y-1.5">
                                    <Label htmlFor="cpf">CPF *</Label>
                                    <Input id="cpf" data-testid="input-cpf" required placeholder="000.000.000-00" value={form.cpf} onChange={set("cpf")} />
                                </div>
                                <div className="space-y-1.5">
                                    <Label htmlFor="email">E-mail *</Label>
                                    <Input id="email" data-testid="input-email" type="email" required value={form.email} onChange={set("email")} />
                                </div>
                                <div className="space-y-1.5">
                                    <Label htmlFor="phone">Telefone (WhatsApp) *</Label>
                                    <Input id="phone" data-testid="input-phone" required placeholder="(31) 99999-9999" value={form.phone} onChange={set("phone")} />
                                </div>
                                <div className="space-y-1.5">
                                    <Label htmlFor="companion_name">Nome do Acompanhante *</Label>
                                    <Input id="companion_name" data-testid="input-companion-name" required value={form.companion_name} onChange={set("companion_name")} />
                                </div>
                                <div className="space-y-1.5">
                                    <Label>Número de hóspedes *</Label>
                                    <Select value={form.guests} onValueChange={set("guests")}>
                                        <SelectTrigger data-testid="select-guests">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {[1, 2, 3, 4].map((n) => (
                                                <SelectItem key={n} value={String(n)} data-testid={`guests-option-${n}`}>
                                                    {n} {n === 1 ? "hóspede" : "hóspedes"}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-1.5">
                                    <Label>Pet (+{formatBRL(PET_FEE)}) *</Label>
                                    <Select value={form.pet} onValueChange={set("pet")}>
                                        <SelectTrigger data-testid="select-pet">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="nao" data-testid="pet-option-nao">Não</SelectItem>
                                            <SelectItem value="sim" data-testid="pet-option-sim">Sim (+{formatBRL(PET_FEE)})</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            <div className="space-y-3">
                                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8B5A2B]">
                                    3. Decoração romântica
                                </p>
                                <Select value={form.decor} onValueChange={(v) => setForm((f) => ({ ...f, decor: v, decor_option: "" }))}>
                                    <SelectTrigger data-testid="select-decor">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="nao" data-testid="decor-option-nao">Não</SelectItem>
                                        <SelectItem value="sim" data-testid="decor-option-sim">Sim</SelectItem>
                                    </SelectContent>
                                </Select>

                                {form.decor === "sim" && (
                                    <div className="space-y-3" data-testid="decor-options">
                                        <img
                                            src="/images/decoracao-romantica.jpeg"
                                            alt="Opções de decoração romântica"
                                            className="w-full rounded-sm border border-[#1B4332]/10"
                                        />
                                        <div className="grid sm:grid-cols-3 gap-3">
                                            {DECOR_OPTIONS.map((opt) => (
                                                <button
                                                    type="button"
                                                    key={opt.id}
                                                    data-testid={`decor-card-${opt.id}`}
                                                    onClick={() => setForm((f) => ({ ...f, decor_option: opt.id }))}
                                                    className={`text-left border rounded-sm p-4 transition-colors duration-300 ${
                                                        form.decor_option === opt.id
                                                            ? "border-[#1B4332] bg-[#1B4332]/5 ring-2 ring-[#1B4332]"
                                                            : "border-[#1B4332]/15 hover:border-[#1B4332]/40"
                                                    }`}
                                                >
                                                    <p className="text-sm font-bold text-[#1B231A]">{opt.name}</p>
                                                    <p className="text-lg font-bold text-[#8B5A2B] mt-1">{formatBRL(opt.price)}</p>
                                                    <ul className="mt-2 space-y-1">
                                                        {opt.items.slice(0, 3).map((item) => (
                                                            <li key={item} className="text-xs text-[#6C7A6B]">· {item}</li>
                                                        ))}
                                                    </ul>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="grid sm:grid-cols-2 gap-4">
                                <p className="sm:col-span-2 text-xs font-bold uppercase tracking-[0.2em] text-[#8B5A2B]">
                                    4. Pagamento
                                </p>
                                <div className="space-y-1.5">
                                    <Label>Forma de pagamento *</Label>
                                    <Select value={form.payment_method} onValueChange={set("payment_method")}>
                                        <SelectTrigger data-testid="select-payment">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="pix" data-testid="payment-option-pix">Pix</SelectItem>
                                            <SelectItem value="cartao" data-testid="payment-option-cartao">
                                                Cartão de crédito (até 6x sem juros)
                                            </SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                {form.payment_method === "cartao" && (
                                    <div className="space-y-1.5">
                                        <Label>Parcelas (sem juros) *</Label>
                                        <Select value={form.installments} onValueChange={set("installments")}>
                                            <SelectTrigger data-testid="select-installments">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {[1, 2, 3, 4, 5, 6].map((n) => (
                                                    <SelectItem key={n} value={String(n)} data-testid={`installments-option-${n}`}>
                                                        {n}x de {total > 0 ? formatBRL(total / n) : "—"} sem juros
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                )}
                                <div className="sm:col-span-2 space-y-1.5">
                                    <Label htmlFor="special_requests">Alguma solicitação especial?</Label>
                                    <Textarea
                                        id="special_requests"
                                        data-testid="input-special-requests"
                                        rows={3}
                                        value={form.special_requests}
                                        onChange={set("special_requests")}
                                    />
                                </div>
                            </div>

                            <div
                                data-testid="booking-total-summary"
                                className="bg-[#1B4332] text-white rounded-sm p-6 space-y-2"
                            >
                                <div className="flex justify-between text-sm">
                                    <span>
                                        {nights} {nights === 1 ? "diária" : "diárias"} x {formatBRL(chalet.price)}
                                    </span>
                                    <span>{formatBRL(stayTotal)}</span>
                                </div>
                                {petPrice > 0 && (
                                    <div className="flex justify-between text-sm">
                                        <span>Taxa pet</span>
                                        <span>{formatBRL(petPrice)}</span>
                                    </div>
                                )}
                                {decorPrice > 0 && (
                                    <div className="flex justify-between text-sm">
                                        <span>Decoração {decorOption?.name}</span>
                                        <span>{formatBRL(decorPrice)}</span>
                                    </div>
                                )}
                                <div className="border-t border-white/20 pt-3 flex justify-between items-center">
                                    <span className="font-bold">Valor total</span>
                                    <span className="text-2xl font-bold" data-testid="booking-total-value">
                                        {formatBRL(total)}
                                    </span>
                                </div>
                                {form.payment_method === "cartao" && total > 0 && (
                                    <p className="text-xs text-white/80 text-right">
                                        ou {installments}x de {formatBRL(total / installments)} sem juros
                                    </p>
                                )}
                            </div>

                            {error && (
                                <p data-testid="booking-error" className="text-sm font-bold text-red-700">
                                    {error}
                                </p>
                            )}

                            <button
                                type="submit"
                                data-testid="booking-submit-button"
                                disabled={!datesSelected || !decorValid || loading}
                                className="w-full bg-[#1B4332] hover:bg-[#2D6A4F] disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold text-sm px-8 py-4 rounded-full transition-colors duration-300 flex items-center justify-center gap-2"
                            >
                                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                                {datesSelected ? "Confirmar solicitação de reserva" : "Selecione as datas no calendário"}
                            </button>
                        </form>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
};
